import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { UserEntityContext } from '../../hooks/DataProductContext';
import { DataQualityCard } from '../DataContractPage/DataQualityCard';
import { DataPreviewCard } from '../DataPreviewCard';
import { DependenciesCard } from '../DependenciesCard';
import { useAccessControlRequestTemplate } from '../OutputsCard/hooks/useAccessControlRequestTemplate';
import { MultiTemplateAccessRequestDialog } from '../OutputsCard/MultiTemplateAccessRequestDialog';
import { resolveTemplateGroups } from '../OutputsCard/hooks/resolveTemplateGroups';
import { QuestionsCard } from '../QuestionsCard/QuestionsCard';
import { ReviewsCard } from '../Rating/ReviewsCard';
import { AclVerb } from '@agilelab/plugin-wb-access-control-common';
import { parseUrn } from '@agilelab/plugin-wb-builder-common';
import { ShoppableMode } from '@agilelab/plugin-wb-marketplace-common';
import {
  customAlertApiRef,
  CustomViewRegister,
  DrawersContextProvider,
  useAction,
  useDataPath,
  useUsername,
  WbAlert,
  WbButton,
  WbCard,
  WbCardContent,
  WbDivider,
  WbCardActions,
  WbTooltip,
} from '@agilelab/plugin-wb-platform';
import {
  scaffolderApiRef,
  scaffolderTasksFinalizer,
} from '@agilelab/plugin-wb-scaffolder';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { Box, Button, Grid, useTheme } from '@material-ui/core';
import { DataContractStatusSummary } from '../../hooks/useDataContractStatus';
import { useOutputPorts } from '../../hooks/useOutputPorts';
import { DataContractStatusCard } from '../DataContractPage/DataContractStatusCard';
import { extractErrorMessageFromDataContractResult } from '../DataContractPage/utils';
import { FlagAndScore } from '../DataProductPage/FlagAndScore/FlagAndScore';
import { MetricResultsOverview } from './MetricResultsOverview';
import { TechnicalInfoCard } from '../generic/TechnicalInfoCard';
import { extractRightIds } from './utils';
import { PolicyViolationsOverview } from './PolicyViolationsOverview';
import { FlagAndScoreContextProvider } from '../DataProductPage/FlagAndScore/FlagAndScoreContext';

const GridItemWrapper = (children: ReactNode) => {
  const theme = useTheme();
  return (
    <Grid
      item
      style={{
        padding: `${theme.spacing(2)}px 0`,
        paddingRight: `${theme.spacing(4)}px`,
        paddingLeft: 0,
      }}
    >
      {children}
    </Grid>
  );
};
// boolean for showing the request access button in the info card
const SHOW_INFO_CARD_REQUEST_ACCESS_BUTTON = false;

function governanceResourceFromData(data: any):
  | {
      id: string;
      externalId: string;
      environment: string;
      name: string;
    }
  | undefined {
  const resourceInfo = data._computedInfo;
  const {
    kind,
    environment: systemEnvironment,
    external_id,
    name,
    id,
  } = resourceInfo || {};

  const isSystem = kind === 'system';
  const isComponent = kind === 'component';

  const environment = isSystem
    ? systemEnvironment?.name
    : data._system?.environment?.name;
  if ((isSystem || isComponent) && external_id && environment && name) {
    return {
      id: id,
      externalId: external_id,
      environment: environment,
      name: name,
    };
  }
  return undefined;
}

CustomViewRegister.register({
  id: 'marketplace_flag_and_score',
  function: function FlagAndScoreComponent(_: Record<string, any>) {
    const data = useDataPath('');
    const governanceResource = governanceResourceFromData(data);

    const resourceInfo = useMemo(() => {
      if (!governanceResource?.externalId || !governanceResource?.environment) {
        return undefined;
      }
      return governanceResource;
    }, [governanceResource]);

    if (resourceInfo) {
      return (
        <DrawersContextProvider>
          <FlagAndScoreContextProvider resourceInfo={resourceInfo}>
            <FlagAndScore />
          </FlagAndScoreContextProvider>
        </DrawersContextProvider>
      );
    }
    return <></>;
  },
});

// TODO https://witboost.atlassian.net/browse/WIT-5672: this is no longer used in default custom views, consider removing if no one is using it
CustomViewRegister.register({
  id: 'marketplace_policy_violations_overview',
  function: function PolicyViolationsOverviewComponent(
    props: Record<string, any>,
  ) {
    const data = useDataPath('');
    const governanceResource = governanceResourceFromData(data);
    if (!governanceResource) return <></>;
    return (
      <PolicyViolationsOverview
        title={props.title}
        policyIdFilter={props.policyIds ?? []}
        hideIfEmpty={props.hideIfEmpty}
        noDataMessage={props.noDataMessage}
        resource={governanceResource}
        wrapper={GridItemWrapper}
      />
    );
  },
});

// TODO https://witboost.atlassian.net/browse/WIT-5672: this is no longer used in default custom views, consider removing if no one is using it
CustomViewRegister.register({
  id: 'marketplace_metric_results_overview',
  function: function MetricResultsOverviewComponent(
    props: Record<string, any>,
  ) {
    const data = useDataPath('');
    const governanceResource = governanceResourceFromData(data);
    if (!governanceResource) return <></>;
    return (
      <MetricResultsOverview
        title={props.title}
        metricIdFilter={props.metricIds ?? []}
        hideIfEmpty={props.hideIfEmpty}
        noDataMessage={props.noDataMessage}
        resource={governanceResource}
        wrapper={GridItemWrapper}
      />
    );
  },
});

CustomViewRegister.register({
  id: 'data_preview',
  function: function PreviewCard(props: Record<string, any>) {
    return (
      <DataPreviewCard
        title={props.title}
        path={props.path}
        noDataLabel={props.noDataLabel}
        hideNoDataIcon={props.hideNoDataIcon}
      />
    );
  },
});

CustomViewRegister.register({
  id: 'marketplace_dependency',
  function: function DepCard() {
    return <DependenciesCard />;
  },
});

CustomViewRegister.register({
  id: 'marketplace_questions',
  function: function QuestCard() {
    return <QuestionsCard />;
  },
});

CustomViewRegister.register({
  id: 'marketplace_review',
  function: function RevCard() {
    return <ReviewsCard />;
  },
});

CustomViewRegister.register({
  id: 'marketplace_tech_card',
  function: function TechCard(props: Record<string, any>) {
    const root = useDataPath('');
    const configPathValue = useDataPath(props.configPath ?? '');
    const title = props.title ?? 'Technical Information';

    // resolve config from configPath if provided, otherwise from props
    const configs =
      props.configPath && configPathValue ? configPathValue : props.configs;

    return (
      <TechnicalInfoCard
        title={title}
        configs={configs}
        descriptor={root ?? {}}
        maxChars={props.maxChars}
        maxLines={props.maxLines}
        showMoreButton={props.showMoreButton}
        wrapper={({ children }) => (
          <Grid item xs={12}>
            {children}
          </Grid>
        )}
      />
    );
  },
});

CustomViewRegister.register({
  id: 'marketplace_component_card',
  function: function OutCard(props: Record<string, any>) {
    const userEntity = useContext(UserEntityContext);
    const alertApi = useApi(customAlertApiRef);
    const scaffolderApi = useApi(scaffolderApiRef);
    const configApi = useApi(configApiRef);
    const isMultipleAccessRequestEnabled = configApi.getOptionalBoolean(
      'mesh.marketplace.multipleAccessRequestSelection',
    );
    const [scaffolderTaskIds, setScaffolderTaskIds] = useState<string[]>([]);
    const [isAccessRequestDialogOpen, setIsAccessRequestDialogOpen] =
      useState(false);
    const data = useDataPath('');
    const clearSelections = useAction('clearSelection');
    const clearSelection = () => clearSelections(data.kind);
    const selectAll = useAction('selectAll');
    const selectedOutputPorts = (data._parent._selectedComponents || []).filter(
      (s: any) =>
        (s.descriptor.kind || '').toLowerCase() ===
        (data.kind || '').toLowerCase(),
    );
    const componentCardUseCaseTemplateIdHierarchy = useMemo(() => {
      const consumableIds = [
        ...new Set(
          selectedOutputPorts
            .map((s: any) => s.descriptor?.useCaseTemplateId)
            .filter(Boolean) as string[],
        ),
      ];
      const productId = data._parent?.useCaseTemplateId as string | undefined;
      return [...consumableIds, ...(productId ? [productId] : [])];
    }, [selectedOutputPorts, data._parent]);
    const grantTemplateResponse = useAccessControlRequestTemplate({
      templateType: 'grant',
      useCaseTemplateIdHierarchy: componentCardUseCaseTemplateIdHierarchy,
    });
    const productUseCaseTemplateId = data._parent?.useCaseTemplateId as
      | string
      | undefined;
    const componentCardTemplateGroups = useMemo(
      () =>
        resolveTemplateGroups(
          selectedOutputPorts,
          grantTemplateResponse.allTemplates,
          productUseCaseTemplateId,
        ),
      [
        selectedOutputPorts,
        grantTemplateResponse.allTemplates,
        productUseCaseTemplateId,
      ],
    );
    const isShoppableSystem =
      data._parent?._computedInfo?.kind === 'system' &&
      data._parent?._computedInfo?.shoppable === ShoppableMode.Shoppable;

    const ClearRequestsButton = () => (
      <Button
        variant="outlined"
        size="small"
        onClick={clearSelection}
        color="primary"
        disabled={!selectedOutputPorts || selectedOutputPorts.length === 0}
      >
        Clear
      </Button>
    );
    const RequestAccessButton = () => (
      <WbTooltip title={grantTemplateResponse.error ?? ''}>
        <span>
          <WbButton
            disabled={
              !selectedOutputPorts ||
              selectedOutputPorts.length === 0 ||
              !grantTemplateResponse.template
            }
            variant="contained"
            onClick={() => setIsAccessRequestDialogOpen(true)}
            size="small"
            color="primary"
            label="Request access"
            fullWidthUnderBreakpoint="w_xs"
          />
        </span>
      </WbTooltip>
    );

    const SelectAllButton = () => (
      <WbTooltip title="Select all">
        <span>
          <WbButton
            variant="outlined"
            onClick={() => selectAll()}
            size="small"
            color="primary"
            label="Select all"
            disabled={isShoppableSystem}
            fullWidthUnderBreakpoint="w_xs"
          />
        </span>
      </WbTooltip>
    );

    const CustomCardHeader = () => (
      <WbCardActions breakpoint="w_xs">
        {isMultipleAccessRequestEnabled && <SelectAllButton />}
        <ClearRequestsButton />
        <WbDivider />
        <RequestAccessButton />
      </WbCardActions>
    );

    useEffect(() => {
      if (scaffolderTaskIds && scaffolderTaskIds.length > 0) {
        scaffolderTasksFinalizer(
          scaffolderTaskIds,
          "A grant access operation has been triggered. You will receive a notification when it's completed",
          scaffolderApi,
          alertApi,
        );
      }
    }, [scaffolderTaskIds, scaffolderApi, alertApi]);

    return (
      <>
        <MultiTemplateAccessRequestDialog
          isOpen={isAccessRequestDialogOpen}
          groups={componentCardTemplateGroups}
          systemInstance={data._parent._computedInfo}
          userEntity={userEntity}
          onRequestSent={() => clearSelection()}
          setIsOpen={setIsAccessRequestDialogOpen}
          setScaffolderTaskIds={setScaffolderTaskIds}
          verb={AclVerb.Grant}
          requestSource="component"
        />
        <WbCard title={props.title} actions={<CustomCardHeader />}>
          <WbCardContent>{props.children}</WbCardContent>
        </WbCard>
      </>
    );
  },
});

CustomViewRegister.register({
  id: 'marketplace_info_card',
  function: function InfoCard(props: Record<string, any>) {
    const alertApi = useApi(customAlertApiRef);
    const entity = useDataPath('');
    const isSystem = entity._computedInfo?.kind === 'system';
    const shoppableMode: ShoppableMode | null | undefined =
      entity._computedInfo?.shoppable;
    const isShoppable = isSystem
      ? shoppableMode === ShoppableMode.Shoppable
      : !!shoppableMode;
    const isSubcomponent = (() => {
      if (!entity.id) {
        return false;
      }
      try {
        return !!parseUrn(entity.id).subcomponent;
      } catch {
        return false;
      }
    })();
    // neither `SHOPPABLE` nor `HAS_SHOPPABLE_CHILD`
    const isShoppableUndefined = !entity._computedInfo?.shoppable;
    const userEntity = useUsername();
    const [isAccessRequestDialogOpen, setIsAccessRequestDialogOpen] =
      useState(false);
    const [scaffolderTaskIds, setScaffolderTaskIds] = useState<string[]>([]);
    const scaffolderApi = useApi(scaffolderApiRef);
    const { selectedOutputPorts } = useOutputPorts(
      userEntity?.username,
      extractRightIds(isSystem, isShoppable, entity),
    );

    useEffect(() => {
      if (scaffolderTaskIds && scaffolderTaskIds.length > 0) {
        scaffolderTasksFinalizer(
          scaffolderTaskIds,
          "A grant access operation has been triggered. You will receive a notification when it's completed",
          scaffolderApi,
          alertApi,
        );
      }
    }, [scaffolderTaskIds, scaffolderApi, alertApi]);

    const infoCardUseCaseTemplateIdHierarchy = useMemo(() => {
      const consumableIds = [
        ...new Set(
          selectedOutputPorts
            .map(op => op.descriptor?.useCaseTemplateId)
            .filter(Boolean) as string[],
        ),
      ];
      const productId = isSystem
        ? (entity.useCaseTemplateId as string | undefined)
        : (entity._system?.descriptor?.useCaseTemplateId as string | undefined);
      return [...consumableIds, ...(productId ? [productId] : [])];
    }, [selectedOutputPorts, isSystem, entity]);

    const grantTemplateResponse = useAccessControlRequestTemplate({
      templateType: 'grant',
      useCaseTemplateIdHierarchy: infoCardUseCaseTemplateIdHierarchy,
    });
    const infoCardProductUseCaseTemplateId = isSystem
      ? (entity.useCaseTemplateId as string | undefined)
      : (entity._system?.descriptor?.useCaseTemplateId as string | undefined);
    const infoCardTemplateGroups = useMemo(
      () =>
        resolveTemplateGroups(
          selectedOutputPorts,
          grantTemplateResponse.allTemplates,
          infoCardProductUseCaseTemplateId,
        ),
      [
        selectedOutputPorts,
        grantTemplateResponse.allTemplates,
        infoCardProductUseCaseTemplateId,
      ],
    );

    const componentNotShoppableError =
      !isSystem && isShoppableUndefined
        ? 'The project owner has not enabled access requests for this consumable'
        : undefined;

    const systemNotShoppableError =
      isSystem && !isShoppable ? 'Access requests are disabled' : undefined;

    const systemShoppableComponentShoppableError =
      !isSystem &&
      !isShoppableUndefined &&
      entity._system?.shoppable === ShoppableMode.Shoppable
        ? `The project owner has not enabled individual access requests for this consumable. However, you can request access to its parent '${entity._system.name}', as a whole. This will grant you access to all the consumables within it, including this one.`
        : undefined;

    const noConsumableChildrenError = !entity._computedInfo?.consumable
      ? `'${entity.name}' does not have any consumable children. Request Access is disabled.`
      : undefined;

    const templateMissingError =
      !grantTemplateResponse.template && !grantTemplateResponse.error
        ? 'There is no registered access control request template. Please, contact the platform team.'
        : undefined;

    const systemTooltip =
      !systemNotShoppableError && isSystem
        ? 'Submit an access request for all the consumables exposed by this project'
        : undefined;

    const componentTooltip =
      !componentNotShoppableError &&
      !systemShoppableComponentShoppableError &&
      !isSystem
        ? 'Submit an access request'
        : undefined;

    const showRequestAccessButton =
      SHOW_INFO_CARD_REQUEST_ACCESS_BUTTON &&
      !isSubcomponent &&
      !(isSystem && !isShoppable);

    const RequestAccessButton = () => (
      <WbTooltip
        title={
          grantTemplateResponse.error ??
          templateMissingError ??
          componentNotShoppableError ??
          systemNotShoppableError ??
          systemShoppableComponentShoppableError ??
          noConsumableChildrenError ??
          systemTooltip ??
          componentTooltip ??
          ''
        }
      >
        <span>
          <WbButton
            disabled={
              !grantTemplateResponse.template ||
              !!componentNotShoppableError ||
              !!systemNotShoppableError ||
              !!systemShoppableComponentShoppableError ||
              !!noConsumableChildrenError
            }
            variant="contained"
            onClick={() => setIsAccessRequestDialogOpen(true)}
            size="small"
            color="primary"
            label="Request access"
          />
        </span>
      </WbTooltip>
    );
    const CustomCardHeader = () => (
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <RequestAccessButton />
      </Box>
    );
    return (
      <>
        <MultiTemplateAccessRequestDialog
          isOpen={isAccessRequestDialogOpen}
          groups={infoCardTemplateGroups}
          systemInstance={isSystem ? entity._computedInfo : entity._system}
          userEntity={userEntity}
          onRequestSent={() => {}}
          setIsOpen={setIsAccessRequestDialogOpen}
          setScaffolderTaskIds={setScaffolderTaskIds}
          verb={AclVerb.Grant}
          requestSource={isSystem ? 'system' : 'component'}
        />
        <WbCard
          actions={showRequestAccessButton ? <CustomCardHeader /> : <></>}
          title={props.title}
          toolsStyle={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '8px 16px',
            wordBreak: 'break-word',
          }}
        >
          <WbCardContent>{props.children}</WbCardContent>
        </WbCard>
      </>
    );
  },
});

CustomViewRegister.register({
  id: 'marketplace_data_contract_error_panel',
  function: function InfoCard(props: Record<string, any>) {
    const data = useDataPath(
      props.path || '_dataContractStatus',
    ) as DataContractStatusSummary;

    const msg = extractErrorMessageFromDataContractResult(data);

    const formatDateTime = (date: string) => {
      const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      };
      return new Intl.DateTimeFormat('en-US', options).format(new Date(date));
    };

    const getLateResultsMessage = () => {
      if (
        !data ||
        !data.isDataContractRoot ||
        data.resultScheduling?.status !== 'LATE'
      )
        return undefined;
      const lastResultTime = data.lastResult?.createdAt;
      const lastResultTimeMsg = lastResultTime ? (
        <>
          <br />
          Latest monitoring time:{' '}
          <strong>{formatDateTime(lastResultTime)}</strong>
        </>
      ) : null;
      return (
        <>
          A monitoring report was due from the data contract guardian by{' '}
          <strong>
            {formatDateTime(data.resultScheduling.expectedResultTime)}
          </strong>{' '}
          to assess this data contract's compliance, yet it has not been
          received.
          {lastResultTimeMsg}
        </>
      );
    };

    const lateResultsMsg = getLateResultsMessage();

    return msg || lateResultsMsg ? (
      <Box display="flex" flexDirection="column" style={{ gap: '8px' }}>
        {msg && (
          <WbAlert
            variant="compact"
            style={{ margin: 0, borderRadius: 4 }}
            message={{
              title: 'Error',
              content: msg,
              type: 'error',
            }}
          />
        )}
        {lateResultsMsg && (
          <WbAlert
            variant="compact"
            style={{ margin: 0, borderRadius: 4 }}
            message={{
              title: 'Late data contract monitoring results',
              content: lateResultsMsg,
              type: 'warning',
            }}
          />
        )}
      </Box>
    ) : (
      <></>
    );
  },
});

// new data contract result component
CustomViewRegister.register({
  id: 'marketplace_data_contract_policy_status',
  function: function InfoCard(props: Record<string, any>) {
    const data = useDataPath(
      props.path || '_dataContractStatus',
    ) as DataContractStatusSummary;

    if (
      !data ||
      !(data.lastResult?.details.parsingOutcome === 'success') ||
      !data.lastResult.details.targetValidations
    )
      return <></>;
    return (
      <DataContractStatusCard
        validations={data.lastResult.details.targetValidations}
      />
    );
  },
});

CustomViewRegister.register({
  id: 'marketplace_data_contract_quality',
  function: function InfoCard(props: Record<string, any>) {
    const data = useDataPath(props.path || '');
    return <DataQualityCard outputPortEntity={data} />;
  },
});
